import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ColDef, GridReadyEvent } from 'ag-grid-community';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

import 'ag-grid-community/styles/ag-theme-alpine.css';
import AuthService from '../services/authService';
import Spinner from '../components/Spinner';
import TeamOnboardingForm from '../components/TeamOnboardingForm';
import TeamAdminsTab, { TeamAdminsTabRef } from '../components/TeamAdminsTab';
import AdminCreateUserForm from '../components/AdminCreateUserForm';
import UpdateUserForm from '../components/UpdateUserForm';
import TeamList from '../components/TeamList';
import AdminUserListTab from '../components/AdminUserListTab';
import AdminInvoiceTab from '../components/invoice/AdminInvoiceTab';
import AdminInvoiceAnalyticsTab from '../components/invoice/AdminInvoiceAnalyticsTab';
import InvoiceAnalyticsSummaryCard from '../components/invoice/InvoiceAnalyticsSummaryCard';
import AdminCheckoutTab from '../components/checkout/AdminCheckoutTab';
import UserSubPage from '../components/admin/subscription/UserSubPage';
import TeamSubPage from '../components/admin/subscription/TeamSubPage';
import ConferenceSubPage from '../components/admin/subscription/ConferenceSubPage';
import CacheQueriesTab from '../components/CacheQueriesTab';
import { fetchAdminInvoiceAnalytics } from '../services/admin/adminInvoiceApi';
import {
  AdminInvoiceAnalyticsKpis,
  AdminInvoiceAnalyticsOpenNow,
} from '../types/adminInvoice';
import { last6MonthsRange } from '../components/invoice/invoiceAnalyticsDates';

// --- Interfaces ---
interface AdminUserListCounts {
  real_users: number;
  test_users: number;
  demo_users: number;
}

interface User {
  id: number;
  username: string;
  email: string;
  created_at: string;
  is_approved: boolean;
  is_rejected: boolean;
  is_test_user?: boolean;
  full_name?: string;
  college_name?: string;
  team_name?: string;
  team_id?: number;
  team_code?: number;
  designation?: string;
  login_count?: number;
  last_login?: string;
}

interface TeamRequest {
  id: number;
  requested_team_name: string;
  email: string;
  request_date: string;
}

interface RecordbookRequest {
  id: number;
  team_name: string;
  data_type: string;
  status: 'requested' | 'uploaded' | string;
  tables_to_create: string[];
  user_email: string;
  created_at?: string;
  uploaded_at?: string | null;
}

interface AdminUserRequest {
  id: number;
  team_id: number;
  team_name: string;
  team_user_count: number;
  sport_code?: string | null;
  user_id: number;
  username: string;
  request_text?: string | null;
  status: string;
  admin_id?: number | null;
  admin_note?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface AnalyticsConfig {
  sports: string[];
  entities: string[];
  query_types: string[]; 
  teams: string[];
}

interface FlatAnalyticsRow {
  userId: string;
  username: string;
  teamName: string;
  sportCode: string;
  entity: string;
  queryType: string;
  query: string;
  count: number;
}

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectProps> = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter(option => 
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleToggleOption = (option: string) => {
    if (option === 'All Teams') {
      onChange(['All Teams']);
      setIsOpen(false);
      setSearchTerm('');
    } else {
      let newSelected = [...selected];
      
      if (newSelected.includes('All Teams')) {
        newSelected = [];
      }

      if (newSelected.includes(option)) {
        newSelected = newSelected.filter(item => item !== option);
      } else {
        newSelected.push(option);
      }

      if (newSelected.length === 0) {
        onChange(['All Teams']);
      } else {
        onChange(newSelected);
      }
    }
  };

  const removeTag = (teamToRemove: string) => {
    const newSelected = selected.filter(item => item !== teamToRemove);
    if (newSelected.length === 0) {
        onChange(['All Teams']);
    } else {
        onChange(newSelected);
    }
  };

  const isAllTeams = selected.includes('All Teams');

  return (
    <div className="flex flex-col relative" ref={dropdownRef}>
      <label className="text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>
      
      <button 
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 text-left flex justify-between items-center"
      >
        <span className="truncate text-gray-600">
            {isAllTeams ? 'Select Teams...' : `${selected.length} Team(s) Selected`}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full bg-white rounded-lg shadow-xl border border-gray-200 mt-1 top-full">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Search teams..."
                onClick={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm('');
                    searchInputRef.current?.focus();
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Options List with Scrolling */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selected.includes(option);
                return (
                  <div 
                    key={option} 
                    onClick={() => handleToggleOption(option)}
                    className={`px-4 py-2 cursor-pointer text-sm flex items-center hover:bg-blue-50 transition-colors ${isSelected ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                  >
                    <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                        {isSelected && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <span className="truncate">{option}</span>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No teams found matching "{searchTerm}"
              </div>
            )}
          </div>

          {/* Footer with selection count */}
          {filteredOptions.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
              {isAllTeams ? 'All teams selected' : `${selected.length} of ${options.length - 1} teams selected`}
            </div>
          )}
        </div>
      )}

      {!isAllTeams && (
        <div className="flex flex-wrap gap-2 mt-3">
            {selected.map(team => (
                <span key={team} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 shadow-sm">
                    {team}
                    <button 
                        onClick={(e) => { e.stopPropagation(); removeTag(team); }}
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </span>
            ))}
            <button 
                onClick={() => onChange(['All Teams'])}
                className="text-xs text-gray-500 hover:text-red-500 underline mt-1 ml-1"
            >
                Clear All
            </button>
        </div>
      )}
    </div>
  );
};

// --- Tab Configuration ---

type MainTab = 'admin-control' | 'user' | 'user-requests' | 'team' | 'subscription' | 'data';
type SubTab =
  | 'team-requests' | 'record-requests' | 'user-seat'
  | 'real-users' | 'test-users' | 'admin-users' | 'create-user' | 'update-user' | 'invoice' | 'invoice-analytics' | 'direct-checkout'
  | 'onboarding' | 'team-admins' | 'team-list' | 'cache-queries'
  | 'team-sub' | 'user-sub' | 'conference-sub' | 'analytics';

const TAB_CONFIG: Record<MainTab, { label: string; subTabs?: { key: SubTab; label: string }[] }> = {
  'admin-control': {
    label: 'Admin Control',
    subTabs: [
      { key: 'create-user', label: 'Create User' },
      { key: 'invoice', label: 'Invoice' },
      { key: 'direct-checkout', label: 'Direct Checkout' },
      { key: 'invoice-analytics', label: 'Payment Analytics' },
    ],
  },
  user: {
    label: 'Users',
    subTabs: [
      { key: 'real-users', label: 'Real Users' },
      { key: 'update-user', label: 'Update User' },
      { key: 'test-users', label: 'Test Users' },
      { key: 'admin-users', label: 'Admin Users' },
    ],
  },
  'user-requests': {
    label: 'User Requests',
    subTabs: [
      { key: 'team-requests', label: 'Team Requests' },
      { key: 'record-requests', label: 'Record Book Requests' },
      { key: 'user-seat', label: 'User Seats Requests' },
    ],
  },
  team: {
    label: 'Team',
    subTabs: [
      { key: 'onboarding', label: 'Team UI' },
      { key: 'team-admins', label: 'Team Admins' },
      { key: 'team-list', label: 'Teams List' },
    ],
  },
  subscription: {
    label: 'Subscriptions',
    subTabs: [
      { key: 'user-sub', label: 'User Sub' },
      { key: 'team-sub', label: 'Team Sub' },
      { key: 'conference-sub', label: 'Conference Sub' },
    ],
  },
  data: {
    label: 'Data',
    subTabs: [
      { key: 'analytics', label: 'Analytics' },
      { key: 'cache-queries', label: 'Cache Queries' },
    ],
  },
};

const MAIN_TAB_KEYS: MainTab[] = ['admin-control', 'user', 'user-requests', 'team', 'subscription', 'data'];

const isAnalyticsView = (main: MainTab, sub: SubTab) => main === 'data' && sub === 'analytics';

const isUserListSubTab = (tab: SubTab) => tab === 'real-users' || tab === 'test-users' || tab === 'admin-users';

const isDemoUserEmail = (email?: string): boolean => {
  if (!email) return false;
  return email.toLowerCase().includes('athlyte.local');
};

const isTestUserAccount = (user: User): boolean => {
  if (isDemoUserEmail(user.email)) return false;
  const username = (user.username || '').toLowerCase();
  return Boolean(user.is_test_user) || username.includes('test');
};

const isRealUserAccount = (user: User): boolean => {
  return !isDemoUserEmail(user.email) && !isTestUserAccount(user);
};

// --- Main Dashboard Component ---

const AdminDashboard: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const teamOnboardingRef = useRef<any>(null);
  const teamAdminsRef = useRef<TeamAdminsTabRef>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [userListCounts, setUserListCounts] = useState<AdminUserListCounts | null>(null);
  const [teamRequests, setTeamRequests] = useState<TeamRequest[]>([]);
  const [recordbookRequests, setRecordbookRequests] = useState<RecordbookRequest[]>([]);
  const [adminUserRequests, setAdminUserRequests] = useState<AdminUserRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mainTab, setMainTab] = useState<MainTab>('user');
  const [subTab, setSubTab] = useState<SubTab>('real-users');
  const [recordbookPendingActions, setRecordbookPendingActions] = useState<Record<number, boolean>>({});
  const [adminUserRequestPendingActions, setAdminUserRequestPendingActions] = useState<Record<number, boolean>>({});
  const [adminUserRequestStatus, setAdminUserRequestStatus] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [adminUserRequestTeamId, setAdminUserRequestTeamId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedUserData, setSelectedUserData] = useState<User | null>(null);
  const [cacheQueriesRefreshKey, setCacheQueriesRefreshKey] = useState(0);
  
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsConfig, setAnalyticsConfig] = useState<AnalyticsConfig | null>(null);

  const [invoiceSummaryLoading, setInvoiceSummaryLoading] = useState(true);
  const [invoiceSummaryError, setInvoiceSummaryError] = useState<string | null>(null);
  const [invoiceSummaryCurrency, setInvoiceSummaryCurrency] = useState('usd');
  const [invoiceSummaryKpis, setInvoiceSummaryKpis] = useState<AdminInvoiceAnalyticsKpis | null>(null);
  const [invoiceSummaryOpenNow, setInvoiceSummaryOpenNow] = useState<AdminInvoiceAnalyticsOpenNow | null>(null);
  const [invoiceListFocusSearch, setInvoiceListFocusSearch] = useState<string | null>(null);
  const [billingChipDismissed, setBillingChipDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('adminBillingChipDismissed') === '1';
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();
  const authService = new AuthService();

  const navigateTo = useCallback((main: MainTab, sub?: SubTab) => {
    setMainTab(main);
    if (sub) {
      setSubTab(sub);
    } else if (TAB_CONFIG[main].subTabs?.length) {
      setSubTab(TAB_CONFIG[main].subTabs![0].key);
    }
  }, []);

  const fetchInvoiceSummary = useCallback(async () => {
    setInvoiceSummaryLoading(true);
    setInvoiceSummaryError(null);
    const range = last6MonthsRange();
    const result = await fetchAdminInvoiceAnalytics({
      date_from: range.date_from,
      date_to: range.date_to,
      source: 'all',
    });
    if (result.data) {
      setInvoiceSummaryCurrency(result.data.currency || 'usd');
      setInvoiceSummaryKpis(result.data.kpis);
      setInvoiceSummaryOpenNow(result.data.open_now);
      setInvoiceSummaryError(null);
    } else {
      setInvoiceSummaryError(result.error || 'Failed to load invoice analytics');
    }
    setInvoiceSummaryLoading(false);
  }, []);

  useEffect(() => {
    void fetchInvoiceSummary();
  }, [fetchInvoiceSummary]);

  const fetchUsers = async () => {
    if (mainTab === 'subscription') {
      setLoading(false);
      setError('');
      return;
    }
    if (subTab === 'create-user' || subTab === 'update-user' || subTab === 'invoice' || subTab === 'invoice-analytics' || subTab === 'direct-checkout' || subTab === 'onboarding' || subTab === 'team-admins' || subTab === 'team-requests' || subTab === 'record-requests' || subTab === 'user-seat' || subTab === 'cache-queries') {
        setLoading(false);
        if (!isAnalyticsView(mainTab, subTab)) return;
    }
    
    setLoading(true);
    setError('');

    try {
      let response;
      if (isUserListSubTab(subTab) || isAnalyticsView(mainTab, subTab)) {
        response = await authService.getAllUsers('all');
      } else {
        setLoading(false);
        return;
      }

      if (response.status === 200) {
        setUsers(response.data.users || []);
        if (response.data.counts) {
          setUserListCounts(response.data.counts as AdminUserListCounts);
        }
      } else {
        if (response.status === 401 || response.status === 403) {
          navigate('/dashboard');
        } else {
          setError(`Error: ${response.data?.message || 'Failed to fetch users'}`);
        }
      }
    } catch (err) {
      setError('An error occurred while fetching users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const response = await authService.getSearchAnalytics();
      if (response.status === 200) {
        // FIX: Unwrap 'root' key if present
        const actualData = response.data.root || response.data;
        setAnalyticsData(actualData);
      } else {
        console.error("Analytics fetch failed", response);
      }
    } catch (err) { console.error(err); }
  };

  const fetchAnalyticsConfig = async () => {
    try {
        if (typeof authService.getAnalyticsConfig !== 'function') {
            console.error("AuthService.getAnalyticsConfig missing");
            return;
        }
        const response = await authService.getAnalyticsConfig(); 
        if (response.status === 200) setAnalyticsConfig(response.data);
    } catch (err) { console.error("Config load error", err); }
  };

  const fetchTeamRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.getTeamRequests();
      if (response.status === 200) {
        setTeamRequests(response.data.requests || []);
      } else {
        setError(`Error: ${response.data?.message || 'Failed to fetch team requests'}`);
      }
    } catch (err) {
      setError('An error occurred while fetching team requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordbookRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.getRecordbookRequests('requested');
      if (response.status === 200) {
        setRecordbookRequests(response.data.requests || []);
      } else {
        setError(`Error: ${response.data?.message || 'Failed to fetch recordbook requests'}`);
      }
    } catch (err) {
      setError('An error occurred while fetching recordbook requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUserRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const resolvedStatus = adminUserRequestStatus === 'all' ? '' : adminUserRequestStatus;
      const teamIdNum = adminUserRequestTeamId.trim() ? Number(adminUserRequestTeamId.trim()) : undefined;
      const response = await authService.getAdminUserRequests(resolvedStatus || undefined, Number.isFinite(teamIdNum as any) ? teamIdNum : undefined);
      if (response.status === 200) {
        setAdminUserRequests(response.data.requests || []);
      } else {
        setError(`Error: ${response.data?.message || 'Failed to fetch user requests'}`);
      }
    } catch (err) {
      setError('An error occurred while fetching user requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAdminUserRequest = async (requestId: number) => {
    setError('');
    setAdminUserRequestPendingActions(prev => ({ ...prev, [requestId]: true }));
    try {
      const response = await authService.approveAdminUserRequest(requestId);
      if (response.status === 200) {
        const updated = response?.data?.request;
        setAdminUserRequests(prev => {
          if (adminUserRequestStatus === 'pending') return prev.filter(r => r.id !== requestId);
          return prev.map(r => r.id === requestId ? { ...r, ...(updated || { status: 'approved' }) } : r);
        });
      } else {
        setError(`Error: ${response.data?.message || 'Failed to approve request'}`);
      }
    } catch (err) {
      setError('An error occurred while approving request');
    } finally {
      setAdminUserRequestPendingActions(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRejectAdminUserRequest = async (requestId: number) => {
    setError('');
    setAdminUserRequestPendingActions(prev => ({ ...prev, [requestId]: true }));
    try {
      const response = await authService.rejectAdminUserRequest(requestId);
      if (response.status === 200) {
        const updated = response?.data?.request;
        setAdminUserRequests(prev => {
          if (adminUserRequestStatus === 'pending') return prev.filter(r => r.id !== requestId);
          return prev.map(r => r.id === requestId ? { ...r, ...(updated || { status: 'rejected' }) } : r);
        });
      } else {
        setError(`Error: ${response.data?.message || 'Failed to reject request'}`);
      }
    } catch (err) {
      setError('An error occurred while rejecting request');
    } finally {
      setAdminUserRequestPendingActions(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleMarkRecordbookUploaded = async (requestId: number) => {
    setError('');
    setRecordbookPendingActions(prev => ({ ...prev, [requestId]: true }));
    try {
      const response = await authService.markRecordbookRequestUploaded(requestId);
      if (response.status === 200) {
        setRecordbookRequests(prev => prev.filter(req => req.id !== requestId));
      } else {
        setError(`Error: ${response.data?.message || 'Failed to mark as uploaded'}`);
      }
    } catch (err) {
      setError('An error occurred while marking as uploaded');
    } finally {
      setRecordbookPendingActions(prev => ({ ...prev, [requestId]: false }));
    }
  };

  useEffect(() => {
    if (mainTab === 'subscription') {
      setLoading(false);
      setError('');
    } else if (isAnalyticsView(mainTab, subTab)) {
      setLoading(true);
      Promise.all([fetchUsers(), fetchAnalyticsConfig(), fetchAnalyticsData()])
        .finally(() => setLoading(false));
    } else if (subTab === 'team-requests') {
      fetchTeamRequests();
    } else if (subTab === 'record-requests') {
      fetchRecordbookRequests();
    } else if (subTab === 'user-seat') {
      fetchAdminUserRequests();
    } else if (subTab !== 'create-user' && subTab !== 'update-user' && subTab !== 'invoice' && subTab !== 'invoice-analytics' && subTab !== 'direct-checkout' && subTab !== 'onboarding' && subTab !== 'team-admins' && subTab !== 'cache-queries') {
      fetchUsers();
    } else {
        setLoading(false);
    }
  }, [mainTab, subTab, adminUserRequestStatus, adminUserRequestTeamId]);

  // Handlers
  const handleRefresh = () => {
    void fetchInvoiceSummary();
    if (subTab === 'onboarding' && teamOnboardingRef.current) {
      teamOnboardingRef.current.resetForm();
    } else if (subTab === 'team-admins' && teamAdminsRef.current) {
      teamAdminsRef.current.refresh();
    } else if (subTab === 'update-user') {
      setSelectedUserId(null);
      setSelectedUserData(null);
      navigateTo('user', 'real-users');
      setTimeout(() => navigateTo('user', 'update-user'), 0);
    } else if (subTab === 'create-user') {
      navigateTo('user', 'real-users');
      setTimeout(() => navigateTo('admin-control', 'create-user'), 0);
    } else if (isAnalyticsView(mainTab, subTab)) {
      setLoading(true);
      Promise.all([fetchAnalyticsConfig(), fetchAnalyticsData()]).finally(() => setLoading(false));
    } else if (subTab === 'team-requests') {
      fetchTeamRequests();
    } else if (subTab === 'record-requests') {
      fetchRecordbookRequests();
    } else if (subTab === 'user-seat') {
      fetchAdminUserRequests();
    } else if (subTab === 'cache-queries') {
      setCacheQueriesRefreshKey((prev) => prev + 1);
    } else if (subTab === 'invoice' || subTab === 'invoice-analytics' || subTab === 'direct-checkout') {
      // Child tabs own their refresh; summary already refreshed above
    } else {
      fetchUsers();
    }
  };

  // Renderers
  const handleEditUser = (user: User) => {
    setSelectedUserId(user.id);
    setSelectedUserData(user);
    navigateTo('user', 'update-user');
  };

  const userListEmptyMessage = useMemo(() => {
    if (subTab === 'real-users') return 'No real users found.';
    if (subTab === 'test-users') return 'No test users found.';
    if (subTab === 'admin-users') return 'No demo users found.';
    return 'No users found.';
  }, [subTab]);

  const userListExportFileName = useMemo(() => {
    if (subTab === 'real-users') return 'real-users.csv';
    if (subTab === 'test-users') return 'test-users.csv';
    if (subTab === 'admin-users') return 'admin-users.csv';
    return 'users.csv';
  }, [subTab]);

  const dateFormatter = (params: any) => {
    if (!params?.value) return '';
    const date = new Date(params.value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  };

  const teamRequestColumnDefs: ColDef[] = [
    { field: 'requested_team_name', headerName: 'Requested Team Name', filter: true, sortable: true },
    { field: 'email', headerName: 'Email', filter: true, sortable: true },
    { field: 'request_date', headerName: 'Request Date', filter: true, sortable: true, cellRenderer: dateFormatter },
  ];

  const recordbookRequestColumnDefs: ColDef[] = [
    { field: 'team_name', headerName: 'Team Name', filter: true, sortable: true },
    { field: 'user_email', headerName: 'User Email', filter: true, sortable: true },
    { field: 'data_type', headerName: 'Data Type', filter: true, sortable: true, width: 140 },
    { field: 'status', headerName: 'Status', filter: true, sortable: true, width: 140, cellRenderer: (params: any) => {
      if (params.value === 'uploaded') return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">Uploaded</span>;
      return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">Requested</span>;
    }},
    { field: 'tables_to_create', headerName: 'Tables', filter: true, sortable: false, valueGetter: (params: any) => (Array.isArray(params.data?.tables_to_create) ? params.data.tables_to_create.join(', ') : '') },
    { field: 'created_at', headerName: 'Created', filter: true, sortable: true, cellRenderer: dateFormatter },
    { field: 'uploaded_at', headerName: 'Uploaded', filter: true, sortable: true, cellRenderer: (params: any) => (params.value ? dateFormatter(params) : '') },
    { field: 'actions', headerName: 'Action', sortable: false, filter: false, width: 140, cellRenderer: (params: any) => {
      const { id, status } = params.data;
      const isPending = recordbookPendingActions[id] || false;
      if (status !== 'requested') return null;
      return (
        <button onClick={() => handleMarkRecordbookUploaded(id)} disabled={isPending} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1 px-3 rounded text-xs flex items-center justify-center min-w-[90px] h-[26px]">
          {isPending ? <Spinner size="sm" className="mx-auto" /> : 'Uploaded'}
        </button>
      );
    }}
  ];

  const adminUserRequestColumnDefs: ColDef[] = [
    { field: 'sport_code', headerName: 'Sport', filter: true, sortable: true, width: 110, valueGetter: (p: any) => (p.data?.sport_code ? String(p.data.sport_code) : '') },
    { field: 'team_name', headerName: 'Team', filter: true, sortable: true },
    { field: 'team_user_count', headerName: 'Users', filter: true, sortable: true, width: 100 },
    { field: 'username', headerName: 'Username', filter: true, sortable: true },
    { field: 'request_text', headerName: 'Request', filter: true, sortable: false, minWidth: 240, valueGetter: (p: any) => (p.data?.request_text ? String(p.data.request_text) : '') },
    { field: 'created_at', headerName: 'Created', filter: true, sortable: true, cellRenderer: dateFormatter, width: 130 },
    { field: 'actions', headerName: 'Actions', sortable: false, filter: false, width: 180, cellRenderer: (params: any) => {
      const { id } = params.data;
      const status = String(params.data?.status || '').toLowerCase();
      const isPending = adminUserRequestPendingActions[id] || false;
      if (status !== 'pending') return null;
      return (
        <div className="flex space-x-2">
          <button onClick={() => handleApproveAdminUserRequest(id)} disabled={isPending} className="bg-green-500 hover:bg-green-600 text-white font-medium py-1 px-3 rounded text-xs flex items-center justify-center min-w-[70px] h-[26px]">
            {isPending ? <Spinner size="sm" className="mx-auto" /> : 'Approve'}
          </button>
          <button onClick={() => handleRejectAdminUserRequest(id)} disabled={isPending} className="bg-red-500 hover:bg-red-600 text-white font-medium py-1 px-3 rounded text-xs flex items-center justify-center min-w-[70px] h-[26px]">
            {isPending ? <Spinner size="sm" className="mx-auto" /> : 'Reject'}
          </button>
        </div>
      );
    }}
  ];

  const defaultColDef = { flex: 1, minWidth: 100, resizable: true };
  const onGridReady = useCallback((params: GridReadyEvent) => { params.api.sizeColumnsToFit(); }, []);

  const displayedUsers = useMemo(() => {
    if (subTab === 'real-users') {
      return users.filter((user) => isRealUserAccount(user));
    }
    if (subTab === 'test-users') {
      return users.filter((user) => isTestUserAccount(user));
    }
    if (subTab === 'admin-users') {
      return users.filter((user) => isDemoUserEmail(user.email));
    }
    return users;
  }, [users, subTab]);

  const userListMetric = useMemo(() => {
    if (!isUserListSubTab(subTab) || !userListCounts) {
      return null;
    }
    if (subTab === 'real-users') {
      return { label: 'Real Users', count: userListCounts.real_users };
    }
    if (subTab === 'test-users') {
      return { label: 'Test Users', count: userListCounts.test_users };
    }
    return { label: 'Admin Users', count: userListCounts.demo_users };
  }, [subTab, userListCounts]);

  const handleFocusInvoiceUser = useCallback((search: string) => {
    setInvoiceListFocusSearch(search);
    navigateTo('admin-control', 'invoice');
  }, [navigateTo]);

  const handleInvoiceFocusConsumed = useCallback(() => {
    setInvoiceListFocusSearch(null);
  }, []);

  const currentSubTabs = TAB_CONFIG[mainTab].subTabs;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header and Tabs */}
      <div className="flex flex-col mb-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-4">
          <h1 className="text-3xl font-bold text-gray-800 shrink-0">Admin Dashboard</h1>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center shrink-0">
            <div className="inline-flex rounded-md shadow-sm">
              {MAIN_TAB_KEYS.map((key, index) => (
                <button
                  key={key}
                  onClick={() => navigateTo(key)}
                  className={`px-4 py-2 text-sm font-medium border-r border-gray-200 ${
                    mainTab === key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  } ${index === 0 ? 'rounded-l-lg' : ''} ${index === MAIN_TAB_KEYS.length - 1 ? 'rounded-r-lg border-r-0' : ''}`}
                >
                  {TAB_CONFIG[key].label}
                </button>
              ))}
            </div>

            {mainTab === 'admin-control' && subTab !== 'invoice-analytics' && !billingChipDismissed && (
              <InvoiceAnalyticsSummaryCard
                loading={invoiceSummaryLoading}
                error={invoiceSummaryError}
                currency={invoiceSummaryCurrency}
                kpis={invoiceSummaryKpis}
                openNow={invoiceSummaryOpenNow}
                onDetails={() => navigateTo('admin-control', 'invoice-analytics')}
                onRetry={() => void fetchInvoiceSummary()}
                onDismiss={() => {
                  try {
                    sessionStorage.setItem('adminBillingChipDismissed', '1');
                  } catch {
                    /* ignore */
                  }
                  setBillingChipDismissed(true);
                }}
              />
            )}

            <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" onClick={handleRefresh}>Refresh</button>
          </div>
        </div>

        {currentSubTabs && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="inline-flex rounded-md shadow-sm flex-wrap">
              {currentSubTabs.map((tab, index) => (
                <button
                  key={tab.key}
                  onClick={() => setSubTab(tab.key)}
                  className={`px-4 py-2 text-sm font-medium border-r border-gray-200 ${
                    subTab === tab.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  } ${index === 0 ? 'rounded-l-lg' : ''} ${index === currentSubTabs.length - 1 ? 'rounded-r-lg border-r-0' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              {subTab === 'user-seat' && (
                <>
                  <select
                    className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg p-2.5"
                    value={adminUserRequestStatus}
                    onChange={(e) => setAdminUserRequestStatus(e.target.value as 'pending' | 'approved' | 'rejected' | 'all')}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="all">All</option>
                  </select>
                  <input
                    className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg p-2.5 w-[160px]"
                    value={adminUserRequestTeamId}
                    onChange={(e) => setAdminUserRequestTeamId(e.target.value)}
                    placeholder="Team ID (optional)"
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && mainTab !== 'subscription' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {loading && mainTab !== 'subscription' && !isUserListSubTab(subTab) && subTab !== 'create-user' && subTab !== 'update-user' && subTab !== 'invoice' && subTab !== 'invoice-analytics' && subTab !== 'direct-checkout' && subTab !== 'onboarding' && subTab !== 'team-admins' && subTab !== 'team-requests' && subTab !== 'record-requests' && subTab !== 'cache-queries' && (
        <div className="flex justify-center items-center h-64"><Spinner /></div>
      )}

      {!loading && subTab === 'team-requests' && (
        <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto mb-6">
          <div className="ag-theme-alpine w-full h-[500px]">
            <AgGridReact ref={gridRef} rowData={teamRequests} columnDefs={teamRequestColumnDefs} defaultColDef={defaultColDef} modules={[AllCommunityModule]} animateRows={true} onGridReady={onGridReady} pagination={true} paginationPageSize={pageSize} paginationPageSizeSelector={[10, 20, 50, 100]} onPaginationChanged={(event) => setPageSize(event.api.paginationGetPageSize())} domLayout="normal" enableCellTextSelection={true} ensureDomOrder={true} />
          </div>
          {teamRequests.length === 0 && <div className="text-center p-8 bg-gray-50 rounded-lg mt-4"><p className="text-gray-500">No team requests.</p></div>}
        </div>
      )}

      {!loading && subTab === 'record-requests' && (
        <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto mb-6">
          <div className="ag-theme-alpine w-full h-[500px]">
            <AgGridReact ref={gridRef} rowData={recordbookRequests} columnDefs={recordbookRequestColumnDefs} defaultColDef={defaultColDef} modules={[AllCommunityModule]} animateRows={true} onGridReady={onGridReady} pagination={true} paginationPageSize={pageSize} paginationPageSizeSelector={[10, 20, 50, 100]} onPaginationChanged={(event) => setPageSize(event.api.paginationGetPageSize())} domLayout="normal" enableCellTextSelection={true} ensureDomOrder={true} />
          </div>
          {recordbookRequests.length === 0 && <div className="text-center p-8 bg-gray-50 rounded-lg mt-4"><p className="text-gray-500">No record requests.</p></div>}
        </div>
      )}

      {!loading && subTab === 'user-seat' && (
        <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto mb-6">
          <div className="ag-theme-alpine w-full h-[500px]">
            <AgGridReact ref={gridRef} rowData={adminUserRequests} columnDefs={adminUserRequestColumnDefs} defaultColDef={defaultColDef} modules={[AllCommunityModule]} animateRows={true} onGridReady={onGridReady} pagination={true} paginationPageSize={pageSize} paginationPageSizeSelector={[10, 20, 50, 100]} onPaginationChanged={(event) => setPageSize(event.api.paginationGetPageSize())} domLayout="normal" enableCellTextSelection={true} ensureDomOrder={true} />
          </div>
          {adminUserRequests.length === 0 && <div className="text-center p-8 bg-gray-50 rounded-lg mt-4"><p className="text-gray-500">No user requests.</p></div>}
        </div>
      )}

      {isUserListSubTab(subTab) && (
        <AdminUserListTab
          key={subTab}
          users={displayedUsers}
          loading={loading}
          emptyMessage={userListEmptyMessage}
          onEditUser={handleEditUser}
          exportFileName={userListExportFileName}
          metricLabel={userListMetric?.label}
          metricCount={userListMetric?.count ?? null}
        />
      )}

      {subTab === 'create-user' && <div className="p-4 bg-white shadow-md rounded-md mb-6"><h2 className="text-xl font-semibold mb-4">Create New User</h2><AdminCreateUserForm onCreateSuccess={() => { navigateTo('user', 'real-users'); fetchUsers(); }} /></div>}
      
      {subTab === 'update-user' && <div className="p-4 bg-white shadow-md rounded-md mb-6"><UpdateUserForm userId={selectedUserId || undefined} initialUserData={selectedUserData || undefined} onUpdateSuccess={() => { setSelectedUserId(null); setSelectedUserData(null); navigateTo('user', 'real-users'); fetchUsers(); }} onCancel={() => { setSelectedUserId(null); setSelectedUserData(null); navigateTo('user', 'real-users'); }} /></div>}

      {subTab === 'invoice' && (
        <AdminInvoiceTab
          focusListSearch={invoiceListFocusSearch}
          onFocusListSearchConsumed={handleInvoiceFocusConsumed}
        />
      )}

      {subTab === 'invoice-analytics' && (
        <AdminInvoiceAnalyticsTab onFocusInvoiceUser={handleFocusInvoiceUser} />
      )}

      {subTab === 'direct-checkout' && <AdminCheckoutTab />}

      {mainTab === 'subscription' && subTab === 'team-sub' && <TeamSubPage />}

      {mainTab === 'subscription' && subTab === 'conference-sub' && <ConferenceSubPage />}

      {mainTab === 'subscription' && subTab === 'user-sub' && <UserSubPage />}
      
      {subTab === 'onboarding' && <TeamOnboardingForm ref={teamOnboardingRef} />}

      {subTab === 'team-admins' && <TeamAdminsTab ref={teamAdminsRef} />}

      {subTab === 'team-list' && !loading && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <TeamList/>
        </div>
      )}
      {subTab === 'cache-queries' && <CacheQueriesTab refreshTrigger={cacheQueriesRefreshKey} />}

      {isAnalyticsView(mainTab, subTab) && !loading && (
        <AnalyticsDashboard rawData={analyticsData} userList={users} config={analyticsConfig} />
      )}
    </div>
  );
};



const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#a4de6c', '#d0ed57', '#8dd1e1'];

interface AnalyticsProps {
  rawData: any;
  userList: User[];
  config: AnalyticsConfig | null;
}

const AnalyticsDashboard: React.FC<AnalyticsProps> = ({ rawData, userList, config }) => {
  const [selectedSport, setSelectedSport] = useState<string>('All');
  const [selectedTeams, setSelectedTeams] = useState<string[]>(['All Teams']); 
  const [selectedEntity, setSelectedEntity] = useState<string>('All');
  const [selectedQueryType, setSelectedQueryType] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'charts' | 'table'>('charts');
  const [chartGroupBy, setChartGroupBy] = useState<'teamName' | 'sportCode' | 'entity' | 'queryType'>('teamName');

  // 1. Flatten Data
  const flatData = useMemo(() => {
    if (!rawData) return [];
    const rows: FlatAnalyticsRow[] = [];
    const getUserInfo = (uid: string) => {
        const u = userList.find(u => u.username === uid || u.id.toString() === uid);
        return { teamName: u?.team_name || 'Unknown Team', username: u?.username || uid };
    };

    Object.entries(rawData).forEach(([userId, sports]: [string, any]) => {
      const { teamName, username } = getUserInfo(userId);
      Object.entries(sports).forEach(([sportCode, entities]: [string, any]) => {
        Object.entries(entities).forEach(([entity, queryTypes]: [string, any]) => {
          Object.entries(queryTypes).forEach(([qType, stats]: [string, any]) => {
            if (Array.isArray(stats.queries)) {
                stats.queries.forEach((q: any) => {
                    rows.push({
                        userId, username, teamName, sportCode, entity,
                        queryType: qType, query: q.query, count: parseInt(q.search_count || '0')
                    });
                });
            }
          });
        });
      });
    });
    return rows;
  }, [rawData, userList]);

  // 2. Filter Options (Strictly from Backend Config)
  const filterOptions = useMemo(() => {
    if (!config) return { teams: [], sports: [], entities: [], queryTypes: [] };
    return {
      teams: config.teams || [],
      sports: config.sports || [],
      entities: config.entities || [],
      queryTypes: config.query_types || []
    };
  }, [config]);

  // 3. Filter Data Logic
  const filteredData = useMemo(() => {
    return flatData.filter(item => {
      const matchSport = selectedSport === 'All' || item.sportCode === selectedSport;
      const isAllTeams = selectedTeams.includes('All Teams');
      const matchTeam = isAllTeams || selectedTeams.includes(item.teamName);
      const matchEntity = selectedEntity === 'All' || item.entity === selectedEntity;
      const matchQType = selectedQueryType === 'All' || item.queryType === selectedQueryType;
      return matchSport && matchTeam && matchEntity && matchQType;
    });
  }, [flatData, selectedSport, selectedTeams, selectedEntity, selectedQueryType]);

  // 4. Aggregation Logic
  const chartData = useMemo(() => {
    const primaryDistribution: Record<string, number> = {};
    let title = '';

    switch(chartGroupBy) {
        case 'teamName': title = 'Search Volume by Team'; break;
        case 'sportCode': title = 'Sport Code Distribution'; break;
        case 'entity': title = 'Entity Distribution (Player vs Team)'; break;
        case 'queryType': title = 'Query Type Distribution'; break;
    }

    filteredData.forEach(row => {
        const key = row[chartGroupBy] as string;
        primaryDistribution[key] = (primaryDistribution[key] || 0) + row.count;
    });

    const primaryChart = Object.entries(primaryDistribution)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

    return { primaryChart, primaryTitle: title };
  }, [filteredData, chartGroupBy]);

  const handleTeamChange = (newSelectedTeams: string[]) => {
    setSelectedTeams(newSelectedTeams);
  };

  const handleSportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSport(e.target.value);
  };

  const renderCustomLabel = (entry: any) => {
    if (entry.percent < 0.03) return null;
    return `${entry.name} (${(entry.percent * 100).toFixed(0)}%)`;
  };

  const totalQueries = useMemo(() => filteredData.reduce((acc, curr) => acc + curr.count, 0), [filteredData]);
  const uniqueQueries = useMemo(() => new Set(filteredData.map(d => d.query)).size, [filteredData]);

  if (!rawData && !config) {
      return <div className="p-8 text-center text-gray-500">Connecting to analytics service...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md min-h-[800px] flex flex-col md:flex-row overflow-hidden">
      {/* 1. LEFT SIDEBAR */}
      <div className="w-full md:w-[300px] lg:w-1/4 bg-gray-50 p-6 border-r border-gray-200 flex flex-col gap-6">
        <h3 className="text-lg font-bold text-gray-700 mb-2 border-b pb-2">Filters</h3>
        {/* Sport Code */}
        <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1">Sport Code</label>
            <select value={selectedSport} onChange={handleSportChange} className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                {filterOptions.sports.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        {/* Team (MULTI-SELECT) */}
        <MultiSelectDropdown label="Team" options={filterOptions.teams} selected={selectedTeams} onChange={handleTeamChange} />
        {/* Entity */}
        <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1">Entity</label>
            <select value={selectedEntity} onChange={e => setSelectedEntity(e.target.value)} className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                {filterOptions.entities.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        {/* Query Type */}
        <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1">Query Type</label>
            <select value={selectedQueryType} onChange={e => setSelectedQueryType(e.target.value)} className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                {filterOptions.queryTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        <div className="mt-auto pt-6 border-t border-gray-200">
            <div className="mb-4">
                <div className="text-xs text-gray-500 uppercase font-semibold">Total Queries</div>
                <div className="text-3xl font-bold text-blue-600">{totalQueries}</div>
            </div>
            <div>
                <div className="text-xs text-gray-500 uppercase font-semibold">Unique Queries</div>
                <div className="text-3xl font-bold text-purple-600">{uniqueQueries}</div>
            </div>
        </div>
      </div>

      {/* 2. RIGHT CONTENT */}
      <div className="flex-1 p-6 flex flex-col">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b pb-4">
             <h2 className="text-xl font-bold text-gray-800">{chartData.primaryTitle}</h2>
             <div className="flex flex-col sm:flex-row gap-3 items-end sm:items-center">
                {viewMode === 'charts' && (
                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-semibold text-gray-600">Group By:</label>
                        <select value={chartGroupBy} onChange={(e) => setChartGroupBy(e.target.value as any)} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-md p-1.5 shadow-sm">
                            <option value="teamName">Team</option>
                            <option value="sportCode">Sport</option>
                            <option value="entity">Entity</option>
                            <option value="queryType">Query Type</option>
                        </select>
                    </div>
                )}
                <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button type="button" onClick={() => setViewMode('charts')} className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-l-lg ${viewMode === 'charts' ? 'bg-blue-600 text-white' : 'bg-white text-gray-900 hover:bg-gray-100'}`}>Visualizations</button>
                    <button type="button" onClick={() => setViewMode('table')} className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-r-lg ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-900 hover:bg-gray-100'}`}>Raw Data</button>
                </div>
             </div>
        </div>

        <div className="flex-1 flex items-center justify-center bg-white">
            {viewMode === 'charts' ? (
                <div className="w-full max-w-4xl h-[600px]">
                     {chartData.primaryChart.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie 
                                    data={chartData.primaryChart} 
                                    cx="50%" cy="50%" 
                                    labelLine={(props) => props.percent >= 0.03}
                                    label={renderCustomLabel} 
                                    outerRadius={220} 
                                    fill="#8884d8" 
                                    dataKey="value"
                                >
                                    {chartData.primaryChart.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                </Pie>
                                <Tooltip formatter={(value: number, name: string) => [`${value} queries`, name]} />
                                <Legend wrapperStyle={{ maxHeight: '120px', overflowY: 'auto', paddingTop: '20px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : <div className="h-full flex items-center justify-center text-gray-400">No data for selected filters</div>}
                </div>
            ) : (
                <div className="ag-theme-alpine w-full h-full min-h-[600px]">
                    <AgGridReact 
                        rowData={filteredData} 
                        columnDefs={[
                        { field: 'query', headerName: 'Search Query', flex: 2, filter: true },
                        { field: 'count', headerName: 'Count', sort: 'desc', flex: 1, sortable: true },
                        { field: 'entity', headerName: 'Entity', flex: 1, filter: true },
                        { field: 'queryType', headerName: 'Type', flex: 1, filter: true },
                        { field: 'teamName', headerName: 'Team', flex: 1, filter: true },
                        { field: 'sportCode', headerName: 'Sport', width: 100, filter: true },
                        ]} 
                        defaultColDef={{ flex: 1, minWidth: 100, resizable: true, sortable: true }} 
                        modules={[AllCommunityModule]} 
                        pagination={true} 
                        paginationPageSize={20}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                    />
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
